import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import config from '../../config';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  LocalShipping as LocalShippingIcon,
  Replay as ReturnIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ProductImage from '../ProductImage';
import ordersService from '../../services/ordersService';
import CreateSavModal from '../savs/CreateSavModal';
import {
  formatDate,
  formatPrice,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  canCancelOrder
} from '../../utils/orderUtils';

/**
 * Composant pour le modal des détails de commande
 */
const OrderDetailsModal = ({
  open,
  order,
  onClose,
  onCancelOrder,
  onValidateOrder,
  onDownloadInvoice,
  onMarkAsShipped,
  onDeleteOrder,
  isAdmin,
}) => {
  const [shipments, setShipments] = useState([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [createSavModalOpen, setCreateSavModalOpen] = useState(false);
  const [expandedKitItemId, setExpandedKitItemId] = useState(null);
  const [returnLabelModalOpen, setReturnLabelModalOpen] = useState(false);
  const [returnScope, setReturnScope] = useState('full');
  const [returnSelectedItemIds, setReturnSelectedItemIds] = useState([]);

  useEffect(() => {
    if (open && order?.id) {
      fetchShipments();
    } else {
      setShipments([]);
    }
  }, [open, order]);

  useEffect(() => {
    if (!open) setExpandedKitItemId(null);
  }, [open]);

  const fetchShipments = async () => {
    setLoadingShipments(true);
    try {
      const data = await ordersService.getShipments(order.id);
      setShipments(data || []);
    } catch (error) {
      console.error("Error fetching shipments", error);
    } finally {
      setLoadingShipments(false);
    }
  };

  const openReturnLabelModal = () => {
    setReturnScope('full');
    setReturnSelectedItemIds([]);
    setReturnLabelModalOpen(true);
  };

  const handleDownloadLabel = async (shipment) => {
    try {
      const blob = await ordersService.downloadLabel(order.id, shipment?.id);
      if (!blob) {
        alert("Impossible de télécharger l'étiquette (réponse vide).");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquette_${order.orderNumber || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading label', error);
      alert("Erreur lors du téléchargement de l'étiquette");
    }
  };

  const toggleReturnItem = (itemId) => {
    setReturnSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleGenerateReturnLabel = async () => {
    if (returnScope === 'partial' && returnSelectedItemIds.length === 0) {
      alert('Veuillez sélectionner au moins un article pour le retour partiel.');
      return;
    }

    setGeneratingLabel(true);
    try {
      const payload =
        returnScope === 'full'
          ? { fullReturn: true }
          : { fullReturn: false, orderItemIds: returnSelectedItemIds };
      await ordersService.generateReturnLabel(order.id, payload);
      setReturnLabelModalOpen(false);
      await fetchShipments();
      alert('Étiquette retour générée avec succès !');
    } catch (error) {
      console.error('Error generating return label', error);
      alert("Erreur lors de la génération de l'étiquette retour");
    } finally {
      setGeneratingLabel(false);
    }
  };

  if (!order) return null;

  const appliedDiscounts = Array.isArray(order.appliedDiscounts) ? order.appliedDiscounts : [];
  const isPartnerCustomer = Boolean(order?.user?.is_partner || order?.user?.partner_grade_id);
  const hasPromoCode = Boolean(order?.discountCode);
  const hasAnyLineDiscount = Array.isArray(order?.items)
    ? order.items.some((item) => {
      const quantity = Number(item?.quantity || 0);
      const unitPriceHt = Number(
        item?.unitPriceHt ?? (item?.totalPriceHt ? item.totalPriceHt / (quantity || 1) : 0)
      );
      const unitPriceTtc = Number(item?.unitPrice ?? 0);
      const originalUnitPriceHt = Math.max(
        unitPriceHt,
        Number(item?.originalUnitPriceHt ?? item?.product?.priceHt ?? item?.variant?.priceHt ?? unitPriceHt)
      );
      const originalUnitPriceTtc = Math.max(
        unitPriceTtc,
        Number(item?.originalUnitPriceTtc ?? item?.product?.priceTtc ?? item?.variant?.priceTtc ?? unitPriceTtc)
      );
      const totalPriceHt = Number(item?.totalPriceHt ?? (unitPriceHt * quantity));
      const totalPriceTtc = Number(item?.totalPrice ?? (unitPriceTtc * quantity));
      const totalDiscountHt = Math.max(0, (originalUnitPriceHt * quantity) - totalPriceHt);
      const totalDiscountTtc = Math.max(0, (originalUnitPriceTtc * quantity) - totalPriceTtc);
      return totalDiscountHt > 0.0001 || totalDiscountTtc > 0.0001;
    })
    : false;
  const hasPromotionApplied = Boolean(
    (order?.promotionId && Number(order.promotionId) > 0) ||
    appliedDiscounts.length > 0 ||
    Number(order?.discountAmount || 0) > 0 ||
    hasAnyLineDiscount
  );
  const globalDiscountAmount = Number(order?.discountAmount || 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 0,
        fontWeight: 600,
        fontSize: '1.25rem'
      }}>
        Détails de la commande {order.orderNumber}
      </DialogTitle>
      <DialogContent sx={{
        p: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '4px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.5)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
        // Pour Firefox
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.1)',
      }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Informations générales */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Informations générales
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Statut:
                      </Typography>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={getStatusLabel(order.status)}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Date de commande:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(order.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Client partenaire:
                      </Typography>
                      <Chip
                        size="small"
                        color={isPartnerCustomer ? 'success' : 'default'}
                        label={
                          isPartnerCustomer
                            ? `Oui${order?.user?.partner_grade_name ? ` (${order.user.partner_grade_name})` : ''}`
                            : 'Non'
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Code promo:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {hasPromoCode ? order.discountCode : 'Aucun'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" color="text.secondary">
                        Promotion appliquée:
                      </Typography>
                      <Chip
                        size="small"
                        color={hasPromotionApplied ? 'success' : 'default'}
                        label={hasPromotionApplied ? (hasAnyLineDiscount ? 'Oui (remise ligne)' : 'Oui') : 'Non'}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Articles commandés */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Articles commandés
                  </Typography>
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => {
                      const quantity = Number(item.quantity || 0)
                      const unitPriceTtc = Number(item.unitPrice ?? 0)
                      const totalPriceTtc = Number(item.totalPrice ?? (unitPriceTtc * quantity))
                      const unitPriceHt = Number(
                        item.unitPriceHt ?? (item.totalPriceHt ? item.totalPriceHt / (quantity || 1) : (unitPriceTtc ? unitPriceTtc / 1.2 : 0))
                      )
                      const totalPriceHt = Number(item.totalPriceHt ?? (unitPriceHt * quantity))

                      const originalUnitPriceHt = Math.max(
                        unitPriceHt,
                        Number(item.originalUnitPriceHt ?? item.product?.priceHt ?? item.variant?.priceHt ?? unitPriceHt)
                      )
                      const originalUnitPriceTtc = Math.max(
                        unitPriceTtc,
                        Number(item.originalUnitPriceTtc ?? item.product?.priceTtc ?? item.variant?.priceTtc ?? unitPriceTtc)
                      )
                      const originalTotalHt = originalUnitPriceHt * quantity
                      const originalTotalTtc = originalUnitPriceTtc * quantity
                      const totalDiscountHt = Math.max(0, originalTotalHt - totalPriceHt)
                      const totalDiscountTtc = Math.max(0, originalTotalTtc - totalPriceTtc)
                      const hasLineDiscount = totalDiscountHt > 0.0001 || totalDiscountTtc > 0.0001

                      return (
                        <Box key={item.id || index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            {/* Image de l'article */}
                            <ProductImage
                              imageUrl={item.variantImage || item.productImage}
                              productName={item.productName}
                              size={60}
                              borderRadius={1}
                            />

                            {/* Informations de l'article */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                                {item.productName}
                                {item.variantName && ` - ${item.variantName}`}
                              </Typography>
                              {item.kitContents && item.kitContents.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => setExpandedKitItemId((prev) => (prev === item.id ? null : item.id))}
                                    endIcon={expandedKitItemId === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{
                                      textTransform: 'none',
                                      color: 'text.secondary',
                                      justifyContent: 'flex-start',
                                      pl: 0,
                                      minHeight: 32,
                                    }}
                                  >
                                    Contenu du kit ({item.kitContents.length} article{item.kitContents.length > 1 ? 's' : ''})
                                  </Button>
                                  <Collapse in={expandedKitItemId === item.id}>
                                    <Box
                                      sx={{
                                        pl: 1,
                                        borderLeft: '2px solid',
                                        borderColor: 'divider',
                                        mt: 0.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                      }}
                                    >
                                      {item.kitContents.map((c, i) => (
                                        <Box
                                          key={i}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            py: 0.5,
                                            borderBottom: i < item.kitContents.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider',
                                          }}
                                        >
                                          <ProductImage
                                            imageUrl={c.productImage}
                                            productName={c.productName}
                                            size={44}
                                            borderRadius={1}
                                          />
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight="medium" noWrap>
                                              {c.productName}
                                              {c.variantName && c.variantName !== '—' && (
                                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                                  {' — '}{c.variantName}
                                                </Box>
                                              )}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Quantité : {c.quantity}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Quantité: {quantity}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box component="span">HT:</Box>
                                {hasLineDiscount && (
                                  <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                    {formatPrice(originalUnitPriceHt)}
                                  </Box>
                                )}
                                <Box component="span">{formatPrice(unitPriceHt)} × {quantity} =</Box>
                                {hasLineDiscount && (
                                  <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                    {formatPrice(originalTotalHt)}
                                  </Box>
                                )}
                                <Box component="span" sx={{ fontWeight: 600 }}>{formatPrice(totalPriceHt)}</Box>
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box component="span">TTC:</Box>
                                {hasLineDiscount && (
                                  <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                    {formatPrice(originalUnitPriceTtc)}
                                  </Box>
                                )}
                                <Box component="span">{formatPrice(unitPriceTtc)} × {quantity} =</Box>
                                {hasLineDiscount && (
                                  <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                    {formatPrice(originalTotalTtc)}
                                  </Box>
                                )}
                                <Box component="span" sx={{ fontWeight: 600 }}>{formatPrice(totalPriceTtc)}</Box>
                              </Typography>
                              {hasLineDiscount && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                                  Remise appliquée: -{formatPrice(totalDiscountHt)} HT / -{formatPrice(totalDiscountTtc)} TTC
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Prix unitaire final: {formatPrice(unitPriceHt)} HT ({formatPrice(unitPriceTtc)} TTC)
                              </Typography>
                            </Box>
                          </Box>
                          {index < order.items.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      )
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun article trouvé dans cette commande.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Adresses */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Adresse de livraison
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{order.deliveryFirstName} {order.deliveryLastName}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.deliveryAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.deliveryPostalCode} {order.deliveryCity}
                  </Typography>
                  <Typography variant="body2">
                    {order.deliveryCountry}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Adresse de facturation
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{order.billingFirstName} {order.billingLastName}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.billingAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.billingPostalCode} {order.billingCity}
                  </Typography>
                  <Typography variant="body2">
                    {order.billingCountry}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Informations de livraison */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    🚚 Informations de livraison
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Debug temporaire */}
                    {console.log('🔍 Order shipping data in modal:', {
                      orderId: order.id,
                      shippingMethodCode: order.shippingMethodCode,
                      shippingPrice: order.shippingPrice,
                      shippingMethodDetails: order.shippingMethodDetails,
                      deliveryPhone: order.deliveryPhone,
                      pickupPointInfo: order.pickupPointInfo,
                      hasAnyShippingData: !!(order.shippingMethodCode || order.shippingPrice || order.deliveryPhone || order.pickupPointInfo)
                    })}

                    {(order.shippingMethodCode || order.shippingPrice || order.deliveryPhone || order.pickupPointInfo) ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Mode de livraison:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {order.shippingMethodDetails?.icon || '🚚'} {order.shippingMethodDetails?.name || order.shippingMethodCode || 'Non spécifié'}
                          </Typography>
                          {order.shippingPrice && (
                            <Typography variant="caption" color="text.secondary">
                              Prix: {formatPrice(order.shippingPrice)}
                            </Typography>
                          )}
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Cette commande a été passée avant l'implémentation du système de livraison détaillé.
                            Les informations de livraison ne sont pas disponibles.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {order.deliveryPhone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Téléphone de livraison:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          📞 {order.deliveryPhone}
                        </Typography>
                      </Grid>
                    )}
                    {order.pickupPointInfo && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Point relais sélectionné:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                          <Typography variant="body2" fontWeight="bold">
                            📍 {order.pickupPointInfo.name || order.pickupPointInfo.nom}
                          </Typography>
                          <Typography variant="body2">
                            {order.pickupPointInfo.address || order.pickupPointInfo.adresse1}
                          </Typography>
                          <Typography variant="body2">
                            {order.pickupPointInfo.postalCode || order.pickupPointInfo.codePostal} {order.pickupPointInfo.city || order.pickupPointInfo.localite}
                          </Typography>
                          {order.pickupPointInfo.distance && (
                            <Typography variant="caption" color="primary">
                              📏 Distance: {order.pickupPointInfo.distance} km
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    )}
                    {order.trackingNumber && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Numéro de suivi:
                        </Typography>
                        <Chip
                          label={order.trackingNumber}
                          color="info"
                          size="small"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Expéditions & Retours */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      📦 Suivi des colis (Expéditions & Retours)
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ReturnIcon />}
                      onClick={openReturnLabelModal}
                      disabled={generatingLabel}
                    >
                      Générer étiquette retour
                    </Button>
                  </Box>

                  {loadingShipments ? (
                    <CircularProgress size={20} />
                  ) : shipments.length > 0 ? (
                    <Box>
                      {shipments.map((shipment) => (
                        <Paper
                          key={shipment.id}
                          variant="outlined"
                          sx={{
                            p: 2,
                            mb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: (theme) =>
                              shipment.type === 'return'
                                ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)
                                : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                            borderColor: 'divider',
                          }}
                        >
                          <Box>
                            <Chip
                              label={shipment.type === 'return' ? "RETOUR" : "EXPÉDITION"}
                              color={shipment.type === 'return' ? "warning" : "primary"}
                              size="small"
                              sx={{ mr: 2 }}
                            />
                            <Typography variant="body2" component="span" fontWeight="bold">
                              {shipment.carrier.toUpperCase()} - {shipment.trackingNumber}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Créé le {new Date(shipment.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                          <Box>
                            {shipment.labelUrl && (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadLabel(shipment)}
                              >
                                Étiquette
                              </Button>
                            )}
                            <Button
                              size="small"
                              href={`https://www.laposte.fr/outils/suivre-vos-envois?code=${shipment.trackingNumber}`}
                              target="_blank"
                              sx={{ ml: 1 }}
                            >
                              Suivre
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune expédition enregistrée pour le moment.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Résumé financier */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Résumé financier
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          color={isPartnerCustomer ? 'success' : 'default'}
                          label={isPartnerCustomer ? 'Client partenaire' : 'Client non partenaire'}
                        />
                        <Chip
                          size="small"
                          color={hasPromoCode ? 'success' : 'default'}
                          label={hasPromoCode ? `Code promo: ${order.discountCode}` : 'Aucun code promo'}
                        />
                        <Chip
                          size="small"
                          color={hasPromotionApplied ? 'success' : 'default'}
                          label={
                            hasPromotionApplied
                              ? (hasAnyLineDiscount ? 'Promotion/remise appliquée (ligne)' : 'Promotion/remise appliquée')
                              : 'Aucune promotion appliquée'
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Sous-total HT:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.subtotal)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Frais de port:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.shippingCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Remise commerciale:
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={globalDiscountAmount > 0 ? 'success.main' : 'text.primary'}
                      >
                        -{formatPrice(globalDiscountAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        TVA:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.taxAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Points fidélité utilisés:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {Number(order.loyaltyPointsUsed || 0)} points
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Remise fidélité:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" color={Number(order.loyaltyDiscountAmount || 0) > 0 ? 'success.main' : 'text.primary'}>
                        -{formatPrice(Number(order.loyaltyDiscountAmount || 0))}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total TTC:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatPrice(order.totalAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Notes */}
            {order.notes && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                    borderColor: 'text.secondary',
                  },
                  transition: 'box-shadow 0.3s ease',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {order.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        {/* Bouton Valider la commande (si status = pending) */}
        {order.status === 'pending' && onValidateOrder && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => onValidateOrder(order.id)}
            sx={{
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
            }}
          >
            Valider la commande
          </Button>
        )}

        {/* Bouton Télécharger la facture (si status = confirmed) */}
        {order.status === 'confirmed' && onDownloadInvoice && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadInvoice(order.id)}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Télécharger la facture
          </Button>
        )}

        {/* Bouton Marquer comme envoyé (si status = confirmed ou processing) */}
        {['confirmed', 'processing'].includes(order.status) && onMarkAsShipped && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LocalShippingIcon />}
            onClick={() => onMarkAsShipped(order.id)}
            sx={{
              backgroundColor: 'secondary.main',
              '&:hover': {
                backgroundColor: 'secondary.dark',
              },
            }}
          >
            Marquer comme envoyé
          </Button>
        )}

        {/* Bouton Créer un SAV */}
        <Button
          variant="contained"
          color="warning"
          startIcon={<AssignmentIcon />}
          onClick={() => setCreateSavModalOpen(true)}
          sx={{
            backgroundColor: 'warning.main',
            '&:hover': {
              backgroundColor: 'warning.dark',
            },
          }}
        >
          Créer un SAV
        </Button>

        {canCancelOrder(order) && (
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => onCancelOrder(order.id)}
            sx={{
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
          >
            Annuler la commande
          </Button>
        )}

        {isAdmin && onDeleteOrder && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              if (window.confirm('Supprimer définitivement cette commande et ses lignes ? Cette action est irréversible.')) {
                onDeleteOrder(order.id);
              }
            }}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.light',
                color: 'error.dark',
              },
            }}
          >
            Supprimer la commande
          </Button>
        )}

        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: 'text.secondary',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              color: 'text.primary',
            },
          }}
        >
          Fermer
        </Button>
      </DialogActions>

      {/* Modal de création de SAV */}
      <CreateSavModal
        open={createSavModalOpen}
        onClose={() => setCreateSavModalOpen(false)}
        onSuccess={() => {
          setCreateSavModalOpen(false);
          // Optionnel: recharger les données de la commande si nécessaire
        }}
        orderId={order?.id}
      />

      {/* Modal choix contenu du retour (étiquette retour) */}
      <Dialog
        open={returnLabelModalOpen}
        onClose={() => !generatingLabel && setReturnLabelModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: 'background.paper' } }}
      >
        <DialogTitle>Contenu du retour</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Indiquez quels articles sont concernés par ce retour.
          </Typography>
          <RadioGroup
            value={returnScope}
            onChange={(e) => setReturnScope(e.target.value)}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              value="full"
              control={<Radio />}
              label="Toute la commande est en retour"
            />
            <FormControlLabel
              value="partial"
              control={<Radio />}
              label="Retour partiel — sélectionner les articles"
            />
          </RadioGroup>
          {returnScope === 'partial' && order?.items?.length > 0 && (
            <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Articles à inclure dans le retour
              </Typography>
              <List dense disablePadding>
                {order.items.map((item) => (
                  <ListItem
                    key={item.id}
                    dense
                    disablePadding
                    sx={{ py: 0.25, cursor: 'pointer' }}
                    onClick={() => toggleReturnItem(item.id)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={returnSelectedItemIds.includes(item.id)}
                        onChange={() => toggleReturnItem(item.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${item.productName}${item.variantName && item.variantName !== 'Kit' ? ` — ${item.variantName}` : ''}`}
                      secondary={`Quantité : ${item.quantity}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReturnLabelModalOpen(false)} disabled={generatingLabel}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateReturnLabel}
            disabled={generatingLabel || (returnScope === 'partial' && returnSelectedItemIds.length === 0)}
            startIcon={generatingLabel ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {generatingLabel ? 'Génération…' : "Générer l'étiquette retour"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OrderDetailsModal;
